import { createAsyncThunk } from '@reduxjs/toolkit';
import { $apiV1 } from '../../config/api';

// Player-PWA Favorites is multi-playlist:
//   1. One permanent "Favorites" entry, pinned at the top, used for
//      individual-event bookmarks (star on event rows). Lazily created on
//      first add and protected from delete in the UI.
//   2. Other entries are saved-from-token playlists — created when the
//      player taps "Save playlist" while watching /player/events/video.
//
// Backend: piggy-backs on /users/playlists. The Favorites entry is
// identified by `title === FAVORITES_TITLE`. Per the API, every video row
// carries `event_id` nested under `details`.

export const FAVORITES_TITLE = 'Favorites';

const extractListVideos = (playlist) => {
  if (!playlist) return [];
  const v = playlist.videos ?? playlist.results?.videos ?? [];
  return Array.isArray(v) ? v : [];
};

const isFavorites = (p) => p?.title === FAVORITES_TITLE;

// Fetches every playlist owned by the caller. The list endpoint returns
// playlists inline with their video rows, so we don't need a separate detail
// call to know what's in each one.
export const loadPlaylistsIndex = createAsyncThunk(
  'playerFavorites/loadIndex',
  async (_, { rejectWithValue }) => {
    try {
      const res = await $apiV1.get('/users/playlists');
      const list = res.data?.results ?? res.data ?? [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to load playlists');
    }
  },
);

// Find the Favorites entry in current state, or create one server-side.
// Used by addEventToFavorites; exposed separately for callers that need the
// id up-front (e.g. saving a token playlist into Favorites would not — but
// we keep the helper exported for future use).
const ensureFavoritesPlaylist = async (getState, dispatch) => {
  const existing = getState().playerFavorites.playlists.find(isFavorites);
  if (existing) return existing;
  const res = await $apiV1.post('/users/playlists', { title: FAVORITES_TITLE });
  const created = res.data;
  dispatch({ type: 'playerFavorites/playlistCreatedLocally', payload: created });
  return created;
};

// Add an event marker to the permanent Favorites playlist. Lazily creates
// the playlist on first call.
export const addEventToFavorites = createAsyncThunk(
  'playerFavorites/addEvent',
  async (event, { getState, dispatch, rejectWithValue }) => {
    try {
      const fav = await ensureFavoritesPlaylist(getState, dispatch);
      const payload = [{
        event_id: Number(event.markerId ?? event.eventId ?? event.id),
        title: event.title || event.paramName || 'Event',
      }];
      await $apiV1.post(`/users/playlists/${fav.id}/add`, payload);
      // Refetch the playlist so we get the new item's playlist-row id (the
      // /add endpoint's response shape is inconsistent across deployments).
      const detail = await $apiV1.get(`/users/playlists/${fav.id}`);
      return { playlistId: fav.id, videos: extractListVideos(detail.data) };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to add favorite');
    }
  },
);

// Remove an event from the Favorites playlist by the playlist-item id (the
// `id` field on a video row, not the marker id). Caller resolves the
// playlist-item id via selectFavoriteVideoIdForEvent.
export const removeEventFromFavorites = createAsyncThunk(
  'playerFavorites/removeEvent',
  async ({ videoId }, { getState, rejectWithValue }) => {
    try {
      const fav = getState().playerFavorites.playlists.find(isFavorites);
      if (!fav || !videoId) return { playlistId: fav?.id ?? null, videoId };
      await $apiV1.delete(`/users/playlists/${fav.id}/${videoId}/`);
      return { playlistId: fav.id, videoId };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to remove favorite');
    }
  },
);

// Rename a saved playlist. Permanent Favorites is protected at the UI layer —
// callers should not dispatch this with the Favorites id (the backend would
// also reject the title collision since FAVORITES_TITLE is unique per user).
export const renamePlaylist = createAsyncThunk(
  'playerFavorites/renamePlaylist',
  async ({ playlistId, title }, { rejectWithValue }) => {
    try {
      if (!playlistId) throw new Error('playlistId is required');
      const trimmed = (title || '').trim();
      if (!trimmed) throw new Error('title is required');
      await $apiV1.put(`/users/playlists/${playlistId}`, { title: trimmed });
      return { playlistId, title: trimmed };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to rename playlist');
    }
  },
);

// Remove a saved playlist entirely. The permanent Favorites row is protected
// at the UI layer — callers should not dispatch this with the Favorites id.
export const removePlaylist = createAsyncThunk(
  'playerFavorites/removePlaylist',
  async ({ playlistId }, { rejectWithValue }) => {
    try {
      if (!playlistId) throw new Error('playlistId is required');
      await $apiV1.delete(`/users/playlists/${playlistId}`);
      return { playlistId };
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to remove playlist');
    }
  },
);

// Save the markers currently bound to a watch-token as a new named playlist.
// Used by the "Save playlist" button on /player/events/video.
export const saveCurrentPlaylist = createAsyncThunk(
  'playerFavorites/saveCurrentPlaylist',
  async ({ title, markerIds }, { rejectWithValue }) => {
    try {
      if (!title || !Array.isArray(markerIds) || markerIds.length === 0) {
        throw new Error('saveCurrentPlaylist requires title and a non-empty markerIds array');
      }
      const createRes = await $apiV1.post('/users/playlists', { title });
      const playlist = createRes.data;
      const playlistId = playlist?.id ?? playlist?.results?.id;
      if (!playlistId) throw new Error('No playlist id returned');
      const items = markerIds.map((id) => ({ event_id: Number(id) }));
      await $apiV1.post(`/users/playlists/${playlistId}/add`, items);
      // Refetch with the now-attached videos so the slice gets a complete row.
      const detail = await $apiV1.get(`/users/playlists/${playlistId}`);
      return detail.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to save playlist');
    }
  },
);
